import { useState } from 'react';
import { Link, Navigate, useNavigate } from 'react-router-dom';
import { useAuth, api } from '@halo/web-lib';
import logoLight from '../assets/logo/icon-text-logo-light.svg';

export function LoginPage() {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const { login, isAuthenticated, user } = useAuth();
  const navigate = useNavigate();

  // Already logged in as admin — go to dashboard
  if (isAuthenticated && String(user?.role) === '0') {
    return <Navigate to="/" replace />;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');
    setLoading(true);

    try {
      const res = await api.post<{
        token: string;
        user: { userId: string; email: string; role: number; displayName: string | null; emailVerified: boolean };
      }>('/api/v1/auth/login', { email, password });

      if (!res.success || !res.data) {
        setError(res.error ?? 'Login failed');
        setLoading(false);
        return;
      }

      // Check admin role before allowing login
      if (String(res.data.user.role) !== '0') {
        setError('Access denied. Admin privileges required.');
        setLoading(false);
        return;
      }

      login(res.data.token, { ...res.data.user, role: String(res.data.user.role) });
      navigate('/', { replace: true });
      return;
    } catch {
      setError('Network error');
    }
    setLoading(false);
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-950 relative overflow-hidden">
      {/* Background decoration */}
      <div className="absolute inset-0">
        <div className="absolute top-0 right-0 w-96 h-96 bg-primary/5 rounded-full -translate-y-1/2 translate-x-1/2" />
        <div className="absolute bottom-0 left-0 w-80 h-80 bg-primary/5 rounded-full translate-y-1/2 -translate-x-1/2" />
      </div>

      <div className="w-full max-w-sm relative z-10">
        <div className="flex justify-center mb-8">
          <img src={logoLight} alt="Halo" className="h-8" />
        </div>

        <div className="bg-gray-900 dark:bg-gray-950 rounded-2xl border border-gray-800 p-8 shadow-2xl">
          <div className="flex items-center gap-2 mb-1">
            <h1 className="text-xl font-bold text-white">Admin Console</h1>
            <span className="text-[10px] font-semibold uppercase tracking-wider bg-primary/20 text-primary px-2 py-0.5 rounded-full">
              Admin
            </span>
          </div>
          <p className="text-sm text-gray-400 dark:text-gray-500 mb-6">Sign in with your admin account</p>

          {error && (
            <div className="px-3 py-2 mb-4 text-sm text-red-400 bg-red-950/50 border border-red-900 rounded-lg">
              {error}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Email</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                autoComplete="email"
                placeholder="admin@halo.com"
                className="block w-full px-3 py-2.5 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-1.5">Password</label>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="current-password"
                placeholder="••••••••"
                className="block w-full px-3 py-2.5 bg-gray-800 dark:bg-gray-800 border border-gray-700 rounded-lg text-sm text-white placeholder-gray-500 focus:ring-2 focus:ring-primary focus:border-transparent outline-none transition-colors"
                required
              />
            </div>
            <div className="text-right">
              <Link
                to="/forgot-password"
                className="text-xs text-gray-400 dark:text-gray-500 hover:text-primary transition-colors"
              >
                Forgot password?
              </Link>
            </div>
            <button
              type="submit"
              disabled={loading}
              className="w-full py-2.5 px-4 bg-primary text-gray-900 dark:text-gray-100 text-sm font-semibold rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
            >
              {loading ? 'Signing in…' : 'Sign In'}
            </button>
          </form>
        </div>

        <p className="text-center text-xs text-gray-600 dark:text-gray-300 mt-6">
          Halo Admin Panel &middot; Authorized personnel only
        </p>
      </div>
    </div>
  );
}
